import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { environment } from '../../../environments/environment';

/**
 * Loads the Meta (Facebook) Pixel and reports PageViews — production web only.
 *
 * Gated on `environment.metaPixelId`, which is set ONLY in environment.prod.ts,
 * so the pixel never loads on local, dev, qa, or the native mobile shell (their
 * envs leave the id empty). This is why we inject at runtime instead of pasting
 * Meta's snippet into index.html, which ships to every environment.
 *
 * Privacy (this is a payroll app): the pixel only reports PageViews on public
 * marketing / auth-entry pages. The authenticated employer/employee areas and
 * any token-bearing links (activation, password reset, email confirmation) are
 * never reported, so no payroll PII or one-time tokens reach Meta.
 */
const PUBLIC_PATHS = new Set<string>([
  '/', '/contact', '/privacy', '/terms', '/auth/login', '/auth/register'
]);

interface Fbq {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: unknown;
  loaded: boolean;
  version: string;
}

@Injectable({ providedIn: 'root' })
export class MetaPixelService {
  private readonly doc    = inject(DOCUMENT);
  private readonly router = inject(Router);
  private started = false;

  /** Idempotent. No-op unless a pixel id is configured (production only). */
  init(): void {
    const id = environment.metaPixelId;
    if (!id || this.started) { return; }
    this.started = true;

    this.loadPixel(id);

    // Report the current page, then each public navigation. The loader below
    // does NOT auto-fire PageView (unlike Meta's copy-paste snippet) so we can
    // gate it by route.
    this.trackIfPublic(this.router.url);
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => this.trackIfPublic(e.urlAfterRedirects));
  }

  private trackIfPublic(url: string): void {
    const path = url.split(/[?#]/)[0] || '/';
    const win = this.doc.defaultView as (Window & { fbq?: Fbq }) | null;
    if (PUBLIC_PATHS.has(path) && win?.fbq) {
      win.fbq('track', 'PageView');
    }
  }

  /** Standard Meta Pixel loader, minus the auto `track('PageView')`. */
  private loadPixel(pixelId: string): void {
    const win = this.doc.defaultView as (Window & { fbq?: Fbq; _fbq?: Fbq }) | null;
    if (!win || win.fbq) { return; }

    const fbq = function (this: unknown, ...args: unknown[]): void {
      if (fbq.callMethod) { fbq.callMethod.apply(fbq, args); }
      else { fbq.queue.push(args); }
    } as Fbq;
    win.fbq = fbq;
    if (!win._fbq) { win._fbq = fbq; }
    fbq.push    = fbq;
    fbq.loaded  = true;
    fbq.version = '2.0';
    fbq.queue   = [];

    const script = this.doc.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    this.doc.head.appendChild(script);

    win.fbq('init', pixelId);
  }
}
